import { useCallback, useEffect, useMemo, useState } from 'react'
import _ from 'lodash'
import { addDays, format } from 'date-fns'
import {
  getHealthieProviderId,
  getHealthieUserId,
} from 'utils/authLocalStorage'
import {
  getHealthieRequest,
  showErrorMessage,
  useApplyTags,
  useCardPay,
} from './careHooks'
import {
  APPOINTMENTS_STRING,
  GET_ORGANIZATION_STRING,
  useHealthieAPIs,
} from 'apis/HealthieApiClient'
import {
  useBookedAppointment,
  useI18n,
  usePersonalInfo,
  useRehabSelectedThemes,
  useSkipToScheduledTime,
  useUnavailableStatus,
} from 'contexts/AuthContext'
import { logBrazeEvent } from 'utils/brazeInit'
import { ScreenKey } from 'views/Care/util'
import { logAmplitudeEvent } from 'utils/amplitudeInit'
import { MaxLimitedDate } from 'views/Care/ScheduledTime'
import { updateUserPromise } from 'apis/FitOnCareAPI'
import { useUserProfileStatus } from './useUserProfileStatus'
import { getTagId } from './useApplyTags'
import { UnavailableStatus } from 'views/Care/Unavailable'

export const filterQualification = (providers, qualification) => {
  return _.filter(providers, (item) => {
    const qualifications =
      item.qualifications
        ?.split(',')
        ?.map((item) => item.trim()?.toLowerCase()) || []
    return qualifications?.includes(qualification)
  })
}

export const ScheduleLanguageOptions = [
  { value: 'en', label: 'English', name: 'English' },
  { value: 'es', label: 'Spanish', name: 'Spanish' },
]

export const useCareCompleteBooking = () => {
  const { i18n } = useI18n()
  const { completeBooking } = useHealthieAPIs()
  const { personalInfo } = usePersonalInfo()
  const { applyHealthieTags } = useApplyTags()

  const completeBookingAppointment = async ({
    slot,
    selectedAppointmentType,
    selectedContactType,
    onSuccess = () => {},
    onFailure = () => {},
  }) => {
    const convertedValues = {
      appointment_id: slot.appointment_id,
      first_name: personalInfo.firstName,
      last_name: personalInfo.lastName,
      email: personalInfo.email,
      phone_number: personalInfo.phone,
      date: slot.date,
      provider_id: slot.user_id,
      appointment_type_id: (selectedAppointmentType || {}).id,
      contact_type: selectedContactType,
      timezone:
        Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
    }

    try {
      const {
        data: { completeCheckout: payload },
      } = await completeBooking({ variables: convertedValues })

      if (!payload.messages && payload.appointment) {
        onSuccess(payload.appointment)
      } else {
        onFailure()
        showErrorMessage(payload, i18n)
      }
    } catch (e) {
      onFailure()
      showErrorMessage({}, i18n)
    }
  }
  return { completeBookingAppointment, personalInfo, applyHealthieTags }
}

export const useAppointment = (props) => {
  const { sku, qualification = 'rd' } = props
  const providerId = getHealthieProviderId()
  const { completeBookingAppointment, personalInfo } = useCareCompleteBooking()
  const { availableSlotsForRange } = useHealthieAPIs()
  const { localCode } = useI18n()

  const [selectedAppointmentType, setSelectedAppointmentType] = useState()
  const [selectedProviderIds] = useState([providerId])
  const [selectedSlot, setSelectedSlot] = useState()
  const { bookedAppointment, setBookedAppointment } = useBookedAppointment()
  const [payLoading, setPayLoading] = useState(false)
  const [bookLoading, setBookPayLoading] = useState(false)
  const [availableInit, setAvailableInit] = useState(true)
  const [providerIds, setProviderIds] = useState()
  const [loadingVisible, setLoadingVisible] = useState(false)
  const [appointmentsCount, setAppointmentsCount] = useState(0)
  const { setSkipToScheduledTime } = useSkipToScheduledTime()
  const { rehabSelectedThemes } = useRehabSelectedThemes()
  const { getUserProfileStatus } = useUserProfileStatus()
  const [isLoading, setIsLoading] = useState(true)
  const [orgData, setOrgData] = useState(null)
  const [orgLoading, setOrgLoading] = useState(false)
  const [selectedLanguage, setSelectedLanguage] = useState()
  const [appointmentLoaded, setAppointmentLoaded] = useState(false)
  const { setUnavailableStatus } = useUnavailableStatus()
  const [scheduleEventLogParams, setScheduleEventLogParams] = useState({})

  const [scheduleLanguage, setScheduleLanguage] = useState(
    _.find(ScheduleLanguageOptions, { value: localCode }) ||
      _.find(ScheduleLanguageOptions, { value: 'en' })
  )

  const { theme } = useMemo(() => {
    return {
      theme: rehabSelectedThemes?.map((theme) => theme.name).join(', '),
    }
  }, [rehabSelectedThemes])

  useEffect(() => {
    setOrgLoading(true)
    getHealthieRequest({
      operationName: 'getOrganization',
      queryString: GET_ORGANIZATION_STRING,
      variables: {
        provider_id: providerId,
        state_licenses: [],
        user_id: getHealthieUserId(),
      },
    })
      .then(({ data }) => {
        setOrgData(data)
      })
      .finally(() => {
        setOrgLoading(false)
      })
  }, [providerId])

  const getAppointmentCount = useCallback(async () => {
    const { data } = await getHealthieRequest({
      queryString: APPOINTMENTS_STRING,
      variables: {
        user_id: getHealthieUserId(),
        sort_by: 'date_asc',
        should_paginate: true,
        with_all_statuses: true,
      },
      operationName: 'appointments',
    })
    let appointmentsCount = 0
    if (data) {
      appointmentsCount = data.appointmentsCount || 0
      setAppointmentsCount(appointmentsCount)
    }
    if (appointmentsCount === 0) {
      logBrazeEvent('Schedule Appointment Start')
    }
  }, [])

  useEffect(() => {
    getAppointmentCount()
  }, [getAppointmentCount])

  const getFilteredProviders = (orgData, scheduleLanguage) => {
    let filteredProvider = filterQualification(
      _.filter(orgData?.organization?.providers || [], (item) => {
        return (
          !_.isEmpty(item.state_licenses) &&
          _.find(item.state_licenses, {
            state: personalInfo.state?.code || '',
          }) &&
          item.has_future_availabilities
        )
      }),
      qualification
    )
    if (scheduleLanguage && scheduleLanguage.value !== 'en') {
      const tagId = getTagId(personalInfo.state?.code, scheduleLanguage.name)
      filteredProvider = _.filter(filteredProvider || [], (item) => {
        return _.find(item.active_tags, {
          id: tagId,
        })
      })
    }
    return filteredProviderByAppointmentType(filteredProvider, qualification)
  }

  const getAppointmentName = (qualification) => {
    switch (qualification) {
      case 'pt':
        return 'Initial Evaluation'
      default:
        return 'Initial Consultation'
    }
  }

  const filteredProviderByAppointmentType = (providers, qualification) => {
    const appointmentName = getAppointmentName(qualification)
    const availableProviders = providers.filter((provider) => {
      return provider.appointment_types.some((type) => {
        if (!type.clients_can_book) return false
        if (type.name !== appointmentName) return false
        if (!type.require_specific_providers) return true
        return type.provider_appt_type_connections.some(
          (conn) => conn.user_id === provider.id
        )
      })
    })
    return availableProviders
  }

  const getSelectedAppointmentType = (providers) => {
    const appointmentName = getAppointmentName(qualification)
    return (
      _.find(_.flatMap(providers, 'appointment_types'), {
        name: appointmentName,
      }) || null
    )
  }

  useEffect(() => {
    if (!orgLoading && orgData) {
      let filteredProvider = getFilteredProviders(orgData, scheduleLanguage)
      setProviderIds(_.map(filteredProvider || [], (item) => item.id))
      const appointmentType = getSelectedAppointmentType(filteredProvider)
      setSelectedAppointmentType(appointmentType)
      setAppointmentLoaded(true)
      if (
        _.isEmpty(filteredProvider) &&
        scheduleLanguage.value !== 'en' &&
        !selectedLanguage
      ) {
        setScheduleLanguage(_.find(ScheduleLanguageOptions, { value: 'en' }))
      }
    }
  }, [orgLoading, orgData, scheduleLanguage, selectedLanguage])

  const { onCardPay } = useCardPay({
    setLoading: setPayLoading,
    product: props.product,
    onPurchaseFailure: () => {
      setPayLoading(false)
      props.setCacheInfo({ ...props.cacheInfo, creditCardError: true })
      props.setCurrentKey(ScreenKey.CASH_PAY)
    },
    onPurchaseSuccess: () => {
      logAmplitudeEvent('Payment: Purchase - Success', {
        Type: 'Self Pay',
      })
      setPayLoading(false)
      setBookPayLoading(true)
      completeBookingAppointment({
        slot: selectedSlot,
        selectedAppointmentType,
        onSuccess: (appointment) => {
          setBookPayLoading(false)
          setBookedAppointment(appointment)
          setSelectedSlot(null)
        },
        onFailure: () => {
          setBookPayLoading(false)
        },
      })
    },
  })

  useEffect(() => {
    if (payLoading || bookLoading) {
      setLoadingVisible(true)
    } else {
      setLoadingVisible(false)
    }
  }, [payLoading, bookLoading])

  const logScheduleEvent = async (
    availableTimeSlots,
    providers,
    chosenLanguage
  ) => {
    const rds = providers.length || 0
    const { shouldSkipFlow } = await getUserProfileStatus()
    let params = {}
    switch (sku) {
      case 'msk':
      case 'pt':
        params = {
          'Available PTs': rds,
          'Available Time Slots': rds === 0 ? 0 : availableTimeSlots,
          sku,
          theme,
          ...(shouldSkipFlow ? { flow_skip: 1 } : {}),
          ...(chosenLanguage ? { chosen_language: chosenLanguage.name } : {}),
        }
        logAmplitudeEvent('Screen View: Onboarding - Schedule', params)
        setScheduleEventLogParams(params)
        break
      case 'wlc':
        params = {
          'Available RDs': rds,
          'Available Time Slots': rds === 0 ? 0 : availableTimeSlots,
          sku: 'wlc',
          ...(shouldSkipFlow ? { flow_skip: 1 } : {}),
          ...(chosenLanguage ? { chosen_language: chosenLanguage.name } : {}),
        }
        logAmplitudeEvent('Screen View: Onboarding - Schedule', params)
        setScheduleEventLogParams(params)
        break
      default:
        params = {
          'Available RDs': rds,
          'Available Time Slots': rds === 0 ? 0 : availableTimeSlots,
          ...(shouldSkipFlow ? { flow_skip: 1 } : {}),
          ...(chosenLanguage ? { chosen_language: chosenLanguage.name } : {}),
          sku,
        }
        logAmplitudeEvent('Screen View: Onboarding - Schedule', params)
        setScheduleEventLogParams(params)
        break
    }
  }

  const logScheduleClickEvent = useCallback(
    (params) => {
      logAmplitudeEvent('Click: Onboarding - Schedule', {
        ...scheduleEventLogParams,
        ...params,
      })
    },
    [scheduleEventLogParams]
  )

  const getAllAvailableTimeSlots = useCallback(
    async (providers) => {
      if (!orgData || !providers) return []
      const { data: slotData } = await availableSlotsForRange({
        variables: {
          org_level: true,
          timezone:
            Intl.DateTimeFormat().resolvedOptions().timeZone ||
            'America/New_York',
          appt_type_id: selectedAppointmentType?.id,
          provider_id: providerId,
          provider_ids: _.map(providers || [], (item) => item.id),
          end_date: format(addDays(new Date(), MaxLimitedDate), 'yyyy-MM-dd'),
          start_date: format(new Date(), 'yyyy-MM-dd'),
          clients_can_join_waitlist: true,
          licensed_in_state: '',
        },
      })
      return slotData?.availableSlotsForRange
    },
    [selectedAppointmentType, providerId, orgData]
  )

  const setProviderEvents = useCallback(async () => {
    if (!orgLoading && orgData && appointmentLoaded) {
      let filteredProvider = getFilteredProviders(orgData, scheduleLanguage)
      const availableSlotsForRange = await getAllAvailableTimeSlots(
        filteredProvider
      )

      const _availableTimeSlots = availableSlotsForRange?.length || 0

      const rds = filteredProvider.length || 0
      switch (sku) {
        case 'msk':
        case 'pt':
          if (rds === 0 || _availableTimeSlots === 0) {
            props.setSavedData({
              noPTs: true,
            })
            setSkipToScheduledTime(true)
            if (sku === 'msk') {
              props.setCurrentKey(ScreenKey.REHAB_CONGRATULATIONS)
            } else {
              updateUserPromise({ skuStatus: { sku: 'pt', status: 8 } })
              setUnavailableStatus(
                UnavailableStatus.LIMITED_AVAILABILITY_SCHEDULED_TIME
              )
              props.setCurrentKey(ScreenKey.UNAVAILABLE)
            }
          } else {
            props.setSavedData({
              noPTs: false,
            })
            logScheduleEvent(
              _availableTimeSlots,
              filteredProvider,
              scheduleLanguage
            )
          }
          break
        case 'wlc':
          if (rds === 0 || _availableTimeSlots === 0) {
            props.setSavedData({
              noRDs: true,
            })
            updateUserPromise({ skuStatus: { sku: 'rd', status: 8 } })
            props.setCurrentKey(ScreenKey.SKU_CONGRATULATIONS)
            setSkipToScheduledTime(true)
          } else {
            props.setSavedData({
              noRDs: false,
            })
            logScheduleEvent(
              _availableTimeSlots,
              filteredProvider,
              scheduleLanguage
            )
          }
          break
        default:
          if (rds === 0 || _availableTimeSlots === 0) {
            props.setSavedData({
              noRDs: true,
            })
            updateUserPromise({ skuStatus: { sku, status: 8 } })
            setSkipToScheduledTime(true)
            setUnavailableStatus(
              UnavailableStatus.LIMITED_AVAILABILITY_SCHEDULED_TIME
            )
            props.setCurrentKey(ScreenKey.UNAVAILABLE)
          } else {
            props.setSavedData({
              noRDs: false,
            })
            logScheduleEvent(
              _availableTimeSlots,
              filteredProvider,
              scheduleLanguage
            )
          }
          break
      }
    }
    setIsLoading(false)
  }, [orgLoading, orgData, sku, scheduleLanguage, appointmentLoaded])

  useEffect(() => {
    if (availableInit && appointmentLoaded) {
      setProviderEvents()
      setAvailableInit(false)
    }
  }, [setProviderEvents, availableInit, appointmentLoaded])

  const onLanguageChange = useCallback(
    async (language) => {
      setSelectedLanguage(language)

      const chosenLanguage =
        _.find(ScheduleLanguageOptions, { value: language }) ||
        _.find(ScheduleLanguageOptions, { value: 'en' })
      setScheduleLanguage(chosenLanguage)
      const filteredProvider = getFilteredProviders(orgData, chosenLanguage)
      const availableSlotsForRange = await getAllAvailableTimeSlots(
        filteredProvider
      )
      logScheduleEvent(
        availableSlotsForRange?.length || 0,
        filteredProvider,
        chosenLanguage
      )
    },
    [orgData, getAllAvailableTimeSlots]
  )

  return {
    selectedAppointmentType,
    selectedProviderIds,
    selectedSlot,
    setSelectedSlot,
    bookedAppointment,
    setBookedAppointment,
    payLoading,
    setPayLoading,
    bookLoading,
    setBookPayLoading,
    availableInit,
    setAvailableInit,
    providerIds,
    setProviderIds,
    loadingVisible,
    setLoadingVisible,
    appointmentsCount,
    setAppointmentsCount,
    setSkipToScheduledTime,
    onCardPay,
    providerId,
    personalInfo,
    completeBookingAppointment,
    isLoading,
    selectedLanguage,
    onLanguageChange,
    setScheduleLanguage,
    scheduleLanguage,
    logScheduleClickEvent,
  }
}
